import { SparklesIcon } from "@heroicons/react/24/outline";
import { type FC } from "react";

import { HoverNote } from "@components/EvalReview/HoverNote";
import { SCORE_SELECTED, SCORE_VALUES } from "@components/EvalReview/constants";
import type { CriterionKey } from "@components/EvalRubric/constants";
import { DIMENSION_HINTS, criterionLabel } from "@components/EvalRubric/constants";

interface ScoreGridProps {
  /** Decided by the server — an authored rubric's, or the built-in set. */
  dimensions: CriterionKey[];
  /**
   * What each score means, keyed by criterion then by level. Per level, not
   * the joined line, so a single level's text can be attached to the numeral
   * it belongs to.
   */
  levelAnchors: Record<string, Record<string, string>>;
  /**
   * The criterion's name as authored, by key.
   *
   * From the server rather than derived from the key: a key is a slug, so
   * "SIB tracking" round-tripped to "Sib tracking". Falls back to the key
   * only when the standard has no label for it.
   */
  labels: Record<string, string>;
  values: Partial<Record<CriterionKey, number>>;
  /** Dimensions still holding the AI reviewer's number, not the reviewer's. */
  fromJudge: ReadonlySet<string>;
  judgeReasons: Record<string, string>;
  /** How many spans each dimension can highlight, shown as an affordance. */
  spanCounts: Record<string, number>;
  activeDimension: CriterionKey | null;
  disabled: boolean;
  onScore: (dimension: CriterionKey, value: number) => void;
  onActivate: (dimension: CriterionKey) => void;
}

/** Label column width, shared by the header and every row so they line up. */
const LABEL_COL = "w-40";

/**
 * One 1-5 row per criterion, including any that were added.
 *
 * The rows come from the server rather than from a list in here, so a
 * criterion someone added is scored like any other — the backend rejects a
 * submission that skips one.
 *
 * What each score means is a tooltip on the numbers — a hover bubble on the
 * column headers for the active criterion, and a `title` on every pill for
 * its own row. It was a list under the grid, then a list above it; both spent
 * about eighty vertical pixels restating the rubric page on every run.
 *
 * The AI's finding shows for the active criterion only. A disclosure listing
 * all six used to sit below the grid, from when the finding rendered down
 * there and five of six were invisible; with the card up against the question
 * and the rows one arrow key apart, it only repeated what clicking through
 * already shows.
 *
 * The question and the AI's finding sit above the grid with their heights
 * reserved. Content that grows between the section label and the rows shifts
 * them under the arrow keys, which defeats the point of having keyboard
 * scoring at all — so anything variable up there gets a fixed slot, and
 * anything that cannot have one goes below.
 *
 * Pills are one neutral colour whether the score is 1 or 5. See
 * SCORE_SELECTED: tinting by value would lead the reviewer.
 */
export const ScoreGrid: FC<ScoreGridProps> = ({
  dimensions,
  levelAnchors,
  labels,
  values,
  fromJudge,
  judgeReasons,
  spanCounts,
  activeDimension,
  disabled,
  onScore,
  onActivate,
}) => {
  const levels = activeDimension ? (levelAnchors[activeDimension] ?? {}) : {};
  const hint = activeDimension ? DIMENSION_HINTS[activeDimension] : "";
  const reasonCount = dimensions.filter((dimension) => judgeReasons[dimension]).length;
  const nameOf = (dimension: string) => labels[dimension] || criterionLabel(dimension);

  return (
    <div>
      {/* Above the scale: the question, then the AI's finding on it. Both
          heights are reserved, because content that grows between the section
          label and the rows shifts them under the arrow keys. Two lines each
          covers the common case; anything longer grows its slot on arrival.

          What a score *means* is not here — it rides on the numerals as a
          tooltip. Six criteria by five levels of prose is more page than it
          is worth, and the rubric page carries the full text.

          The finding's slot exists only when the judge scored something, so a
          run it never reached gets no dead space. */}
      <div className="mb-3 flex max-w-2xl flex-col gap-y-2.5">
        <div className="min-h-10">{hint ? <p className="text-sm font-medium text-text-primary">{hint}</p> : null}</div>

        {reasonCount > 0 ? (
          <div className="min-h-16">
            {activeDimension && judgeReasons[activeDimension] ? (
              /* Tinted and sparkled rather than prefixed "AI reviewer:" — the
                 row already carries an AI marker, so the words were saying
                 twice what the treatment says once. The icon is decorative,
                 so the attribution is kept for screen readers, which cannot
                 see a background colour. */
              <div className="flex items-start gap-x-2 rounded-lg bg-ai-subtle p-3">
                <SparklesIcon className="mt-0.5 size-4 shrink-0 text-ai" aria-hidden="true" />
                <p className="text-sm text-text-primary">
                  <span className="sr-only">AI reviewer: </span>
                  {judgeReasons[activeDimension]}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-x-1.5">
        <span className={`${LABEL_COL} shrink-0`} />
        {SCORE_VALUES.map((value) => {
          const meaning = levels[String(value)]?.trim();
          if (!meaning) {
            return (
              <span key={value} className="w-7 cursor-default text-center text-xs text-text-tertiary">
                {value}
              </span>
            );
          }
          return (
            <HoverNote
              key={value}
              className="w-7 text-center text-xs text-text-tertiary"
              note={
                <>
                  <span className="font-medium">{value}</span> — {meaning}
                </>
              }
            >
              <span className="underline decoration-dotted decoration-1 underline-offset-4">{value}</span>
            </HoverNote>
          );
        })}
      </div>

      <div className="mt-0.5">
        {dimensions.map((dimension) => {
          const isActive = dimension === activeDimension;
          const scored = values[dimension] !== undefined;

          return (
            <div
              key={dimension}
              className={`flex items-center gap-x-1.5 rounded-r py-1.5 pl-2 ${
                // A left accent rather than a ring on the label. The label is
                // a button, so the browser's focus outline drew a rounded box
                // around the text that read as an editable input.
                isActive ? "border-l-2 border-rio-600 bg-bg-surface" : "border-l-2 border-transparent"
              }`}
            >
              <button
                type="button"
                // Blur after a pointer click (detail > 0), so focus returns
                // to the page and the key bindings resume — the guard rightly
                // leaves keys alone while a control is focused, and these
                // controls are buttons, so clicking a pill used to freeze
                // 1-5/arrows/Enter until focus moved. A keyboard-synthesised
                // click (detail 0) keeps focus, so Tab users stay where they
                // are.
                onClick={(event) => {
                  if (event.detail > 0) event.currentTarget.blur();
                  onActivate(dimension);
                }}
                className={`${LABEL_COL} shrink-0 truncate rounded text-left text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-rio-600/32 ${
                  isActive ? "font-medium text-text-primary" : scored ? "text-text-primary" : "text-text-secondary"
                } hover:text-rio-600`}
              >
                {nameOf(dimension)}
              </button>

              {SCORE_VALUES.map((value) => {
                const selected = values[dimension] === value;
                const unconfirmed = selected && fromJudge.has(dimension);
                const meaning = (levelAnchors[dimension] ?? {})[String(value)]?.trim();
                return (
                  <button
                    key={value}
                    type="button"
                    disabled={disabled}
                    title={meaning ? `${value} — ${meaning}` : undefined}
                    aria-label={`${nameOf(dimension)}: ${value}${meaning ? `, ${meaning}` : ""}${
                      unconfirmed ? " (suggested by the AI reviewer)" : ""
                    }`}
                    aria-pressed={selected}
                    onClick={(event) => {
                      if (event.detail > 0) event.currentTarget.blur();
                      onScore(dimension, value);
                    }}
                    className={`size-7 shrink-0 rounded-full border text-xs transition-colors ${
                      // A pre-filled value is drawn as an outline, not a
                      // solid: it is the AI's answer awaiting confirmation,
                      // and drawing it like a committed score would hide
                      // that nobody has looked at it yet.
                      unconfirmed
                        ? "border-ai border-dashed bg-ai-subtle text-ai"
                        : selected
                          ? SCORE_SELECTED
                          : "border-border-medium bg-bg-surface hover:border-rio-600 hover:bg-rio-50"
                    } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                  >
                    <span aria-hidden="true">{selected ? "●" : ""}</span>
                  </button>
                );
              })}

              {fromJudge.has(dimension) ? (
                <HoverNote
                  className="ml-1.5 shrink-0 rounded bg-ai-subtle px-2 py-0.5 text-xs text-ai"
                  note="Scored by the AI reviewer, not yet confirmed. Pick a number to make it yours."
                >
                  AI
                </HoverNote>
              ) : null}

              {spanCounts[dimension] ? (
                <span className="ml-auto shrink-0 pr-1 text-xs text-globemallow-700">
                  {spanCounts[dimension]} highlighted above
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
